import type { YourRentalRenterProfileDto } from '@zg-rentals/ts-your-rental-api-client';
import { trackEvent } from '.';
import { getRenterProfileInfo } from '@zg-rentals/clickstream';

export const YourRentalPageTriggerSource = {
  YOUR_RENTAL: 'your_rental',
  RENT_TAB: 'rent_tab',
  DOCUMENTS_TAB: 'documents_tab',
  ENROLL_NOW: 'enroll_now',
  REQUEST_ZILLOW_PAYMENTS: 'request_zillow_payments',
} as const;
export type YourRentalPageTriggerSource =
  (typeof YourRentalPageTriggerSource)[keyof typeof YourRentalPageTriggerSource];

export const YourRentalPageTriggerObject = {
  NO_TRIGGER_OBJECT: 'no_trigger_object',
  RENT_REPORTING_UPSELL: 'rent_reporting_upsell',
  CREDITCLIMB_UPSELL: 'creditclimb_upsell',
  ZRM_PAYMENTS_UPSELL: 'zrm_payments_upsell',
} as const;
export type YourRentalPageTriggerObject =
  (typeof YourRentalPageTriggerObject)[keyof typeof YourRentalPageTriggerObject];

// https://dataportal.zillowgroup.net/event-registry/8933
export const emitYourRentalPageView = (renterProfile?: YourRentalRenterProfileDto) =>
  trackEvent({
    envelope: {
      event_template_id: '582',
      event_template_version_id: '1',
      event_type_id: '8933',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'your_rental',
      trigger_type_nm: 'view',
      trigger_object_nm: YourRentalPageTriggerObject.NO_TRIGGER_OBJECT,
      trigger_source_nm: YourRentalPageTriggerSource.YOUR_RENTAL,
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: [],
    },
    ...getRenterProfileInfo(renterProfile),
  });

// https://dataportal.zillowgroup.net/event-registry/8934
export const emitYourRentalTabClick = (
  triggerSource: typeof YourRentalPageTriggerSource.RENT_TAB | typeof YourRentalPageTriggerSource.DOCUMENTS_TAB,
  renterProfile?: YourRentalRenterProfileDto,
) =>
  trackEvent({
    envelope: {
      event_template_id: '582',
      event_template_version_id: '1',
      event_type_id: '8934',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'your_rental',
      trigger_type_nm: 'interaction',
      trigger_object_nm: YourRentalPageTriggerObject.NO_TRIGGER_OBJECT,
      trigger_source_nm: triggerSource,
    },
    semantic: {
      semantic_event_nm: 'your_rental_nav',
      topic_tag_txt: [],
    },
    ...getRenterProfileInfo(renterProfile),
  });

// https://dataportal.zillowgroup.net/event-registry/8935
export const emitRentReportingUpsellView = (renterProfile?: YourRentalRenterProfileDto) =>
  trackEvent({
    envelope: {
      event_template_id: '582',
      event_template_version_id: '1',
      event_type_id: '8935',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'your_rental',
      trigger_type_nm: 'impression',
      trigger_object_nm: YourRentalPageTriggerObject.RENT_REPORTING_UPSELL,
      trigger_source_nm: YourRentalPageTriggerSource.YOUR_RENTAL,
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: [],
    },
    ...getRenterProfileInfo(renterProfile),
  });

// https://dataportal.zillowgroup.net/event-registry/8936
export const emitCreditClimbEnroll = (renterProfile?: YourRentalRenterProfileDto) =>
  trackEvent({
    envelope: {
      event_template_id: '582',
      event_template_version_id: '1',
      event_type_id: '8936',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'your_rental',
      trigger_type_nm: 'interaction',
      trigger_object_nm: YourRentalPageTriggerObject.CREDITCLIMB_UPSELL,
      trigger_source_nm: YourRentalPageTriggerSource.ENROLL_NOW,
    },
    semantic: {
      semantic_event_nm: 'creditclimb_enroll',
      topic_tag_txt: [],
    },
    ...getRenterProfileInfo(renterProfile),
  });

// https://dataportal.zillowgroup.net/event-registry/8937
export const emitRequestZillowPayments = (renterProfile?: YourRentalRenterProfileDto) =>
  trackEvent({
    envelope: {
      event_template_id: '582',
      event_template_version_id: '1',
      event_type_id: '8937',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'your_rental',
      trigger_type_nm: 'interaction',
      trigger_object_nm: YourRentalPageTriggerObject.ZRM_PAYMENTS_UPSELL,
      trigger_source_nm: YourRentalPageTriggerSource.REQUEST_ZILLOW_PAYMENTS,
    },
    semantic: {
      semantic_event_nm: 'request_zillow_payments',
      topic_tag_txt: [],
    },
    ...getRenterProfileInfo(renterProfile),
  });
